import { Card, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Skeleton, Stack, Typography } from "@mui/material";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { NewFormContext } from "../../context/NewFormContext";
import GenericAutoComplete from "../GenericAutoComplete";
import AttToken from "../../helpers/attToken";
import Users, { UserResponse } from "../../service/Users";
import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import {
  handleSetTempo,
  handleChangeDisparaChamadoPausado,
  handleChangeUsers,
  calcularTempoTotal,
  handleChangeTempo,
  converterMilissegundos,
} from "../helpers/Checklists/ConfigPausa";
import { useState } from "react";

const usersService = new Users();

export default function ConfigPausa() {
  const { t } = useTranslation("translation");
  const { modelo, setModelo } = useContext(NewFormContext);
  const [tempoMaxPausa, setTempoMaxPausa] = useState({ dia: 0, hora: 0, minuto: 0 });
  const [tempoMaxExecucao, setTempoMaxExecucao] = useState({ dia: 0, hora: 0, minuto: 0 });

  useEffect(() => {
    if (modelo?.tempoMaxPausa !== undefined) {
      const { dia, hora, minuto } = converterMilissegundos(modelo.tempoMaxPausa);
      setTempoMaxPausa({ dia, hora, minuto });
    }
    if (modelo?.tempoMaxExecucao !== undefined) {
      const { dia, hora, minuto } = converterMilissegundos(modelo.tempoMaxExecucao);
      setTempoMaxExecucao({ dia, hora, minuto });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["GET_USERS"],
    queryFn: async () => {
      const token = await AttToken();
      if (token) {
        const promise = toast.promise(
          async () => {
            const body = { ...token };
            const result: UserResponse = await usersService.getAllUsers(body);
            if (!result.users) {
              throw new Error(t("gestaoUsuarios.erroBuscaUsuarios"));
            }
            return result.users;
          },
          {
            error: t("gestaoUsuarios.erroBuscaUsuarios"),
          },
          { toastId: "fetching-users" },
        );
        return promise;
      }
    },
    retry: 2,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const pausaTotal = calcularTempoTotal(tempoMaxPausa);

    if (modelo?.tempoMaxPausa !== pausaTotal && pausaTotal !== 0) {
      handleSetTempo(pausaTotal, "tempoMaxPausa", setModelo);
    }

    const execucaoTotal = calcularTempoTotal(tempoMaxExecucao);

    if (modelo?.tempoMaxExecucao !== execucaoTotal && execucaoTotal !== 0) {
      handleSetTempo(execucaoTotal, "tempoMaxExecucao", setModelo);
    }
  }, [tempoMaxPausa, tempoMaxExecucao, modelo, setModelo]);

  const onLoading = isLoading || isFetching;

  function Loading() {
    return (
      <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={2} width="100%">
        <Skeleton variant="text" width={350} height={30} />
        <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={2} width="100%">
          <Skeleton variant="rounded" width={80} height={40} /> -
          <Skeleton variant="rounded" width={80} height={40} /> :
          <Skeleton variant="rounded" width={80} height={40} />
        </Stack>
        <Skeleton variant="text" width={350} height={30} />
        <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={2} width="100%">
          <Skeleton variant="rounded" width={80} height={40} /> -
          <Skeleton variant="rounded" width={80} height={40} /> :
          <Skeleton variant="rounded" width={80} height={40} />
        </Stack>
        <Skeleton variant="text" width={250} />
        <Skeleton variant="text" width={350} height={30} />
        <Skeleton variant="rounded" width={250} height={40} />
        <Skeleton variant="text" width={350} height={30} />
        <Skeleton variant="rounded" width={250} height={40} />
      </Stack>
    );
  }

  return (
    <Card sx={{ width: "100%", padding: 2 }}>
      <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={2} width="100%">
        <Typography color="textDisabled" variant="h5" textAlign="center" fontWeight={600} width="100%">
          {t("checklists.configPausa")}
        </Typography>
        {onLoading ? (
          <Loading />
        ) : (
          <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={2} width="100%">
            <Typography variant="h6" fontWeight="bold" color="textDisabled">
              {t("checklists.tempoMaxPausa")}
            </Typography>
            <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={2} width="100%">
              <FormControl sx={{ minWidth: 80 }}>
                <InputLabel>{t("checklists.dia")}</InputLabel>
                <Select
                  value={tempoMaxPausa.dia}
                  label={t("checklists.dia")}
                  onChange={(event) => handleChangeTempo(event.target.value, setTempoMaxPausa, "dia")}>
                  <MenuItem value={0}>0</MenuItem>
                  <MenuItem value={1}>1</MenuItem>
                  <MenuItem value={2}>2</MenuItem>
                </Select>
              </FormControl>{" "}
              &nbsp;-
              <FormControl sx={{ minWidth: 80 }}>
                <InputLabel>{t("checklists.hora")}</InputLabel>
                <Select
                  value={tempoMaxPausa.hora}
                  label={t("checklists.hora")}
                  onChange={(event) => handleChangeTempo(event.target.value, setTempoMaxPausa, "hora")}>
                  {Array.from({ length: 24 }, (_, i) => (
                    <MenuItem key={i} value={i}>
                      {i}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              &nbsp;:
              <FormControl sx={{ minWidth: 80 }}>
                <InputLabel>{t("checklists.minuto")}</InputLabel>
                <Select
                  value={tempoMaxPausa.minuto}
                  label={t("checklists.minuto")}
                  onChange={(event) => handleChangeTempo(event.target.value, setTempoMaxPausa, "minuto")}>
                  {Array.from({ length: 60 }, (_, i) => (
                    <MenuItem key={i} value={i}>
                      {i}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <Typography variant="h6" fontWeight="bold" color="textDisabled">
              {t("checklists.tempoMaxExecucao")}
            </Typography>
            <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={2} width="100%">
              <FormControl sx={{ minWidth: 80 }}>
                <InputLabel>{t("checklists.dia")}</InputLabel>
                <Select
                  value={tempoMaxExecucao.dia}
                  label={t("checklists.dia")}
                  onChange={(event) => handleChangeTempo(event.target.value, setTempoMaxExecucao, "dia")}>
                  <MenuItem value={0}>0</MenuItem>
                  <MenuItem value={1}>1</MenuItem>
                  <MenuItem value={2}>2</MenuItem>
                </Select>
              </FormControl>{" "}
              &nbsp;-
              <FormControl sx={{ minWidth: 80 }}>
                <InputLabel>{t("checklists.hora")}</InputLabel>
                <Select
                  value={tempoMaxExecucao.hora}
                  label={t("checklists.hora")}
                  onChange={(event) => handleChangeTempo(event.target.value, setTempoMaxExecucao, "hora")}>
                  {Array.from({ length: 24 }, (_, i) => (
                    <MenuItem key={i} value={i}>
                      {i}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>{" "}
              &nbsp;:
              <FormControl sx={{ minWidth: 80 }}>
                <InputLabel>{t("checklists.minuto")}</InputLabel>
                <Select
                  value={tempoMaxExecucao.minuto}
                  label={t("checklists.minuto")}
                  onChange={(event) => handleChangeTempo(event.target.value, setTempoMaxExecucao, "minuto")}>
                  {Array.from({ length: 60 }, (_, i) => (
                    <MenuItem key={i} value={i}>
                      {i}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <FormControlLabel
              control={
                <Checkbox
                  checked={modelo?.disparaChamadoPausado ?? false}
                  onChange={(event) => handleChangeDisparaChamadoPausado(event, setModelo)}
                />
              }
              label={t("checklists.disparaChamadoPausado")}
            />
            <Typography variant="h6" fontWeight="bold" color="textDisabled">
              {t("checklists.notificaPausa")}
            </Typography>
            <GenericAutoComplete
              label={t("teamManage.modal.users")}
              options={data?.map((user) => ({ label: user.nome, value: user.id })) ?? []}
              selectedValues={modelo?.notificaPausa ?? []}
              onChange={(values) => handleChangeUsers(values, "notificaPausa", setModelo)}
            />
            <Typography variant="h6" fontWeight="bold" color="textDisabled">
              {t("checklists.retomaExec")}
            </Typography>
            <GenericAutoComplete
              label={t("teamManage.modal.users")}
              options={data?.map((user) => ({ label: user.nome, value: user.id })) ?? []}
              selectedValues={modelo?.retomaExec ?? []}
              onChange={(values) => handleChangeUsers(values, "retomaExec", setModelo)}
            />
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
